import { useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { toast } from "sonner";
import { MapPin, Loader2, MailCheck, ArrowLeft } from "lucide-react";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { useAuth, formatApiError } from "@/context/AuthContext";

export default function ForgotPasswordPage() {
  const { user } = useAuth();
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  if (user) return <Navigate to="/" replace />;

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setSubmitted(true);
      toast.success(data.message || "Bağlantı gönderildi", { duration: 6000 });
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid grid-cols-1 lg:grid-cols-2 bg-white">
      <div className="hidden lg:block relative overflow-hidden bg-zinc-900">
        <img
          src="https://images.unsplash.com/photo-1635111057505-3b7dcc2b72fb?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzV8MHwxfHNlYXJjaHwzfHxyZWFsJTIwZXN0YXRlJTIwcHJvcGVydHklMjBhYnN0cmFjdHxlbnwwfHx8fDE3ODUwMzM1NTh8MA&ixlib=rb-4.1.0&q=85"
          alt="Ekspertiz"
          className="absolute inset-0 h-full w-full object-cover opacity-80"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-zinc-950/70 via-zinc-900/40 to-transparent" />
        <div className="relative z-10 p-12 h-full flex flex-col justify-between text-white">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 grid place-items-center bg-white text-zinc-900 rounded-sm">
              <MapPin className="h-5 w-5" strokeWidth={2.5} />
            </div>
            <span className="text-xl tracking-tight font-semibold">Kolay Arşivim</span>
          </div>
          <div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.05]">
              Şifrenizi <br /> yenileyin.
            </h1>
            <p className="mt-6 text-base text-zinc-200 max-w-md">
              Kayıtlı e-posta adresinize güvenli bir sıfırlama bağlantısı gönderiyoruz. Bağlantı 1 saat boyunca geçerli.
            </p>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center px-8 py-12">
        <div className="w-full max-w-sm">
          <Link
            to="/giris"
            className="inline-flex items-center gap-1.5 text-xs text-zinc-500 hover:text-zinc-900 mb-6"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-3.5 w-3.5" />
            Giriş sayfasına dön
          </Link>
          <div className="mb-6">
            <h2 className="text-3xl font-bold tracking-tight text-zinc-900">Şifremi Unuttum</h2>
            <p className="mt-2 text-sm text-zinc-500">
              Hesabınıza bağlı e-posta adresini girin, sıfırlama bağlantısını gönderelim.
            </p>
          </div>

          {submitted ? (
            <div
              className="border border-zinc-200 rounded-sm p-5 bg-zinc-50"
              data-testid="forgot-password-success"
            >
              <div className="flex items-center gap-2 text-zinc-900 font-semibold">
                <MailCheck className="h-5 w-5" />
                E-postanızı kontrol edin
              </div>
              <p className="mt-2 text-sm text-zinc-600 leading-relaxed">
                Eğer <strong>{email}</strong> ile kayıtlı bir hesap varsa, sıfırlama bağlantısını
                kısa süre içinde göndereceğiz. E-posta gelmezse spam/junk klasörünüzü kontrol edin.
              </p>
              <p className="mt-3 text-xs text-zinc-500">
                Bağlantı 1 saat geçerlidir ve tek kullanımlıktır.
              </p>
            </div>
          ) : (
            <form className="space-y-5" onSubmit={handleSubmit} data-testid="forgot-password-form">
              <div className="space-y-2">
                <Label htmlFor="email">E-posta</Label>
                <Input
                  id="email"
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="forgot-email-input"
                  placeholder="ornek@parsel.com"
                  autoFocus
                />
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm"
                data-testid="forgot-submit-btn"
              >
                {loading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  "Sıfırlama Bağlantısı Gönder"
                )}
              </Button>
            </form>
          )}

          <p className="mt-8 text-[11px] text-zinc-400 leading-relaxed">
            Güvenlik nedeniyle hesabın var olup olmadığı gizli tutulur. Her durumda
            aynı yanıt görüntülenir.
          </p>
        </div>
      </div>
    </div>
  );
}
